import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { meetingsAPI, chatAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Mic,
  Upload,
  Loader2,
  CheckCircle,
  Pause,
  Play,
  Square,
  Sparkles,
  MessageSquare,
  Send,
  FileText,
  X,
  Users,
} from 'lucide-react';

const ANALYSIS_INTERVAL = 30; // seconds between chunk analyses

const SPEAKER_COLORS = [
  'border-blue-500/50',
  'border-emerald-500/50',
  'border-amber-500/50',
  'border-pink-500/50',
  'border-cyan-500/50',
  'border-orange-500/50',
];
const SPEAKER_TEXT_COLORS = [
  'text-blue-400',
  'text-emerald-400',
  'text-amber-400',
  'text-pink-400',
  'text-cyan-400',
  'text-orange-400',
];

const Recording = () => {
  const { t } = useApp();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ title: '', description: '' });
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [status, setStatus] = useState('ready');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [meetingId, setMeetingId] = useState(null);
  const [liveMode, setLiveMode] = useState(false);

  // Live analysis state
  const [liveTranscript, setLiveTranscript] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [speakers, setSpeakers] = useState([]);
  const [topics, setTopics] = useState([]);
  const [speakerCount, setSpeakerCount] = useState(0);
  const [nextAnalysisIn, setNextAnalysisIn] = useState(0);

  // Chat state
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Refs — main recording
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);
  const isMountedRef = useRef(true);
  const transcriptEndRef = useRef(null);

  // Refs — chunk analysis (live mode)
  const streamRef = useRef(null);
  const chunkRecorderRef = useRef(null);
  const chunkChunksRef = useRef([]);
  const chunkTimerRef = useRef(null);
  const chunkNumberRef = useRef(0);
  const transcriptRef = useRef([]);
  const nextAnalysisTimeRef = useRef(0);

  // Direct refs for async callbacks (avoid stale closures)
  const meetingIdRef = useRef(null);
  const isRecordingRef = useRef(false);
  const isPausedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
      if (chunkTimerRef.current) clearInterval(chunkTimerRef.current);
      if (chunkRecorderRef.current && chunkRecorderRef.current.state !== 'inactive') {
        try { chunkRecorderRef.current.stop(); } catch (e) {}
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getSpeakerColor = (speaker) => {
    const idx = speakers.indexOf(speaker);
    return SPEAKER_COLORS[idx >= 0 ? idx % SPEAKER_COLORS.length : 0];
  };

  const getSpeakerTextColor = (speaker) => {
    const idx = speakers.indexOf(speaker);
    return SPEAKER_TEXT_COLORS[idx >= 0 ? idx % SPEAKER_TEXT_COLORS.length : 0];
  };

  // ==================== CHUNK ANALYSIS (LIVE MODE) ====================

  const startChunkRecorder = () => {
    if (!streamRef.current || !streamRef.current.active) return;
    chunkChunksRef.current = [];
    try {
      const chunkMimeTypes = ['audio/webm', 'audio/mp4', 'audio/ogg', 'audio/wav'];
      const chunkMime = chunkMimeTypes.find(m => MediaRecorder.isTypeSupported(m)) || '';
      const recorder = new MediaRecorder(streamRef.current, chunkMime ? { mimeType: chunkMime } : {});
      chunkRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunkChunksRef.current.push(e.data);
      };
      recorder.start(1000);
    } catch (e) {
      console.error('Failed to start chunk recorder:', e);
    }
  };

  const stopAndAnalyzeChunk = () => {
    if (!chunkRecorderRef.current || chunkRecorderRef.current.state === 'inactive') return;

    chunkRecorderRef.current.onstop = async () => {
      const chunkType = chunkRecorderRef.current?.mimeType || 'audio/webm';
      const blob = new Blob(chunkChunksRef.current, { type: chunkType });
      const mId = meetingIdRef.current;

      if (blob.size > 1000 && mId) {
        setAnalyzing(true);
        try {
          const result = await meetingsAPI.analyzeChunk(mId, blob);
          if (result.segments?.length > 0) {
            const newSegments = result.segments.map((seg) => ({
              speaker: seg.speaker || 'Speaker',
              text: seg.text || '',
              chunkNum: chunkNumberRef.current,
            }));
            transcriptRef.current = [...transcriptRef.current, ...newSegments];
            setLiveTranscript([...transcriptRef.current]);
            setTimeout(() => transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
          }
          if (result.speakers?.length > 0) {
            setSpeakers((prev) => [...new Set([...prev, ...result.speakers])]);
          }
          if (result.topics?.length > 0) {
            setTopics((prev) => [...new Set([...prev, ...result.topics])]);
          }
          if (result.speaker_count > 0) setSpeakerCount(result.speaker_count);
        } catch (e) {
          console.error('Chunk analysis error:', e);
        } finally {
          setAnalyzing(false);
        }
      }

      chunkNumberRef.current++;

      // Restart chunk recorder if still actively recording
      if (isRecordingRef.current && !isPausedRef.current && isMountedRef.current) {
        startChunkRecorder();
        nextAnalysisTimeRef.current = Date.now() + ANALYSIS_INTERVAL * 1000;
      }
    };

    try {
      chunkRecorderRef.current.stop();
    } catch (e) {}
  };

  // ==================== RECORDING ====================

  const startRecording = async () => {
    if (liveMode && !formData.title.trim()) {
      toast.error(t('recording.enterTitle') || 'Please enter a meeting title first');
      return;
    }

    try {
      let mId = null;
      // In live mode, create meeting upfront for chunk analysis + chat
      if (liveMode && formData.title.trim()) {
        const meeting = await meetingsAPI.create({
          title: formData.title,
          description: formData.description,
          language: 'auto',
        });
        mId = meeting.id;
        setMeetingId(mId);
        meetingIdRef.current = mId;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Main recorder — captures full audio for final upload
      // Use mp4 on Safari/iPhone (no webm support), webm elsewhere
      const mimeTypes = ['audio/webm', 'audio/mp4', 'audio/ogg', 'audio/wav'];
      const supportedMime = mimeTypes.find(m => MediaRecorder.isTypeSupported(m)) || '';
      mediaRecorderRef.current = new MediaRecorder(stream, supportedMime ? { mimeType: supportedMime } : {});
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      };

      mediaRecorderRef.current.start(1000);

      isRecordingRef.current = true;
      isPausedRef.current = false;
      setIsRecording(true);
      setStatus('recording');
      setRecordingTime(0);
      setLiveTranscript([]);
      setTopics([]);
      setSpeakers([]);
      setSpeakerCount(0);
      setNextAnalysisIn(ANALYSIS_INTERVAL);
      transcriptRef.current = [];
      chunkNumberRef.current = 0;

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
        // Update countdown for next analysis
        if (nextAnalysisTimeRef.current > 0) {
          const remaining = Math.max(0, Math.ceil((nextAnalysisTimeRef.current - Date.now()) / 1000));
          setNextAnalysisIn(remaining);
        }
      }, 1000);

      // Start chunk analysis in live mode
      if (liveMode && mId) {
        startChunkRecorder();
        nextAnalysisTimeRef.current = Date.now() + ANALYSIS_INTERVAL * 1000;
        chunkTimerRef.current = setInterval(() => {
          stopAndAnalyzeChunk();
        }, ANALYSIS_INTERVAL * 1000);
      }

      toast.success(t('recording.started') || 'Recording started');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause();
      isPausedRef.current = true;
      setIsPaused(true);
      clearInterval(timerRef.current);
      if (liveMode) {
        if (chunkTimerRef.current) clearInterval(chunkTimerRef.current);
        // Analyze what we have so far, then stop
        stopAndAnalyzeChunk();
      }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume();
      isPausedRef.current = false;
      setIsPaused(false);
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
        if (nextAnalysisTimeRef.current > 0) {
          const remaining = Math.max(0, Math.ceil((nextAnalysisTimeRef.current - Date.now()) / 1000));
          setNextAnalysisIn(remaining);
        }
      }, 1000);
      if (liveMode) {
        startChunkRecorder();
        nextAnalysisTimeRef.current = Date.now() + ANALYSIS_INTERVAL * 1000;
        chunkTimerRef.current = setInterval(() => {
          stopAndAnalyzeChunk();
        }, ANALYSIS_INTERVAL * 1000);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      isRecordingRef.current = false;
      isPausedRef.current = false;

      if (liveMode) {
        if (chunkTimerRef.current) clearInterval(chunkTimerRef.current);
        // Final chunk analysis
        if (chunkRecorderRef.current && chunkRecorderRef.current.state !== 'inactive') {
          stopAndAnalyzeChunk();
        }
        nextAnalysisTimeRef.current = 0;
        setNextAnalysisIn(0);
      }

      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      setStatus('stopped');
      clearInterval(timerRef.current);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      const isValidExtension = ['.mp3', '.wav', '.webm', '.ogg', '.m4a', '.mp4', '.flac', '.aac', '.caf'].some((ext) =>
        fileName.endsWith(ext)
      );
      const isValidType = file.type.startsWith('audio/') || file.type.startsWith('video/') || file.type === '';
      if (isValidExtension || isValidType) {
        setAudioBlob(file);
        setStatus('stopped');
        toast.success(`Audio file "${file.name}" selected!`);
      } else {
        toast.error('Invalid file type. Please upload MP3, WAV, WebM, M4A, CAF, or FLAC.');
      }
    }
  };

  const handleSubmit = async () => {
    if (!audioBlob) {
      toast.error('Please record or upload audio first');
      return;
    }

    setStatus('uploading');

    try {
      let mId = meetingId;
      // If no meeting yet (non-live mode or file upload), create now
      if (!mId) {
        if (!formData.title.trim()) {
          toast.error('Please enter a meeting title');
          setStatus('stopped');
          return;
        }
        const meeting = await meetingsAPI.create({
          title: formData.title,
          description: formData.description,
          language: 'auto',
        });
        mId = meeting.id;
        setMeetingId(mId);
        meetingIdRef.current = mId;
      }

      setStatus('processing');
      const blobType = audioBlob.type || 'audio/webm';
      const extMap = { 'audio/mp4': 'm4a', 'audio/ogg': 'ogg', 'audio/wav': 'wav' };
      const ext = extMap[blobType] || 'webm';
      const file = new File([audioBlob], `meeting-${mId}.${ext}`, { type: blobType });
      await meetingsAPI.uploadAudio(mId, file, setUploadProgress);

      toast.success('Meeting processed successfully!');
      navigate(`/meeting/${mId}`);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to process meeting');
      setStatus('stopped');
    }
  };

  // ==================== CHAT ====================

  const handleSendChat = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || chatLoading || !meetingId) return;

    const userMessage = chatInput.trim();
    setChatMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setChatInput('');
    setChatLoading(true);

    try {
      const data = await chatAPI.send(userMessage, meetingId);
      setChatMessages((prev) => [...prev, { role: 'assistant', content: data.response }]);
    } catch {
      toast.error('Chat error');
    } finally {
      setChatLoading(false);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  // ==================== RENDER ====================

  const hasLiveContent = liveMode && (liveTranscript.length > 0 || analyzing || isRecording);
  const showRightPanel = hasLiveContent || showChat;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background">
      <div className={`mx-auto px-4 sm:px-6 lg:px-8 py-8 ${showRightPanel ? 'max-w-6xl' : 'max-w-2xl'}`}>
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold tracking-tight" data-testid="recording-title">
            {t('recording.title')}
          </h1>
          <p className="text-muted-foreground mt-2">{t('recording.subtitle')}</p>
        </div>

        <div className={`${showRightPanel ? 'grid grid-cols-1 lg:grid-cols-2 gap-6' : ''}`}>
          {/* Left Column: Recording controls */}
          <div className="space-y-6">
            {/* Meeting Details Form */}
            <Card className="rounded-2xl border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle>Meeting Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">{t('recording.meetingTitle')}</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder={t('recording.meetingTitlePlaceholder')}
                    className="h-11 rounded-lg"
                    disabled={isRecording || status === 'processing'}
                    data-testid="meeting-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">{t('recording.description')}</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={t('recording.descriptionPlaceholder')}
                    className="rounded-lg resize-none"
                    rows={2}
                    disabled={isRecording || status === 'processing'}
                    data-testid="meeting-description-input"
                  />
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-800">
                  <div>
                    <Label htmlFor="live-mode" className="text-sm font-medium">
                      {t('recording.liveAnalysis') || 'Live Analysis'}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t('recording.liveAnalysisDesc') || 'AI transcription with speaker identification every 30s'}
                    </p>
                  </div>
                  <Switch
                    id="live-mode"
                    checked={liveMode}
                    onCheckedChange={setLiveMode}
                    disabled={isRecording || status === 'processing' || status === 'stopped'}
                    data-testid="live-mode-switch"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Recording Section */}
            <Card className="rounded-2xl border-slate-200 dark:border-slate-800">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center justify-center py-6">
                  {/* Timer */}
                  <div className="text-4xl font-mono font-bold mb-4" data-testid="recording-timer">
                    {formatTime(recordingTime)}
                  </div>

                  {/* Audio Waveform */}
                  {isRecording && !isPaused && (
                    <div className="flex items-center justify-center gap-1 h-12 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <div
                          key={i}
                          className="w-1.5 bg-red-500 rounded-full audio-bar"
                          style={{ animationDelay: `${i * 0.1}s` }}
                        />
                      ))}
                    </div>
                  )}

                  {/* Status */}
                  <div className="flex items-center gap-2 mb-6 text-sm">
                    {status === 'ready' && (
                      <>
                        <div className="w-3 h-3 rounded-full bg-slate-400" />
                        <span className="text-muted-foreground">{t('recording.status.ready')}</span>
                      </>
                    )}
                    {status === 'recording' && !isPaused && (
                      <>
                        <div className="w-3 h-3 rounded-full bg-red-500 recording-pulse" />
                        <span className="text-red-500 font-medium">{t('recording.status.recording')}</span>
                        {liveMode && nextAnalysisIn > 0 && (
                          <span className="text-muted-foreground ml-2">
                            {analyzing
                              ? t('recording.analyzing') || 'Analyzing...'
                              : `${t('recording.nextAnalysis') || 'Next analysis in'} ${nextAnalysisIn}s`}
                          </span>
                        )}
                      </>
                    )}
                    {status === 'recording' && isPaused && (
                      <>
                        <div className="w-3 h-3 rounded-full bg-amber-500" />
                        <span className="text-amber-500 font-medium">Paused</span>
                      </>
                    )}
                    {status === 'stopped' && (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-green-500 font-medium">{t('recording.status.stopped')}</span>
                      </>
                    )}
                    {(status === 'uploading' || status === 'processing') && (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
                        <span className="text-violet-500 font-medium">
                          {status === 'processing' ? t('recording.status.processing') : t('recording.status.uploading')}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Progress Bar */}
                  {(status === 'uploading' || status === 'processing') && (
                    <div className="w-full max-w-xs mb-6">
                      <Progress value={status === 'processing' ? 100 : uploadProgress} className="h-2" />
                      <p className="text-center text-sm text-muted-foreground mt-2">
                        {status === 'processing' ? 'Processing with AI...' : `${uploadProgress}%`}
                      </p>
                    </div>
                  )}

                  {/* Controls */}
                  <div className="flex items-center gap-4">
                    {!isRecording && status === 'ready' && (
                      <Button
                        size="lg"
                        onClick={startRecording}
                        className="h-16 w-16 rounded-full bg-red-500 hover:bg-red-600 shadow-lg"
                        disabled={liveMode && !formData.title.trim()}
                        data-testid="start-recording-button"
                      >
                        <Mic className="w-6 h-6" />
                      </Button>
                    )}

                    {isRecording && !isPaused && (
                      <>
                        <Button
                          size="lg"
                          onClick={pauseRecording}
                          variant="outline"
                          className="h-14 w-14 rounded-full"
                          data-testid="pause-recording-button"
                        >
                          <Pause className="w-5 h-5" />
                        </Button>
                        <Button
                          size="lg"
                          onClick={stopRecording}
                          className="h-16 w-16 rounded-full bg-slate-800 hover:bg-slate-900 dark:bg-slate-200 dark:hover:bg-slate-100 dark:text-slate-800"
                          data-testid="stop-recording-button"
                        >
                          <Square className="w-6 h-6" />
                        </Button>
                      </>
                    )}

                    {isRecording && isPaused && (
                      <>
                        <Button
                          size="lg"
                          onClick={resumeRecording}
                          className="h-14 w-14 rounded-full bg-green-500 hover:bg-green-600"
                          data-testid="resume-recording-button"
                        >
                          <Play className="w-5 h-5" />
                        </Button>
                        <Button
                          size="lg"
                          onClick={stopRecording}
                          className="h-16 w-16 rounded-full bg-slate-800 hover:bg-slate-900 dark:bg-slate-200 dark:hover:bg-slate-100 dark:text-slate-800"
                          data-testid="stop-recording-button"
                        >
                          <Square className="w-6 h-6" />
                        </Button>
                      </>
                    )}

                    {status === 'stopped' && (
                      <div className="flex flex-col items-center gap-4">
                        {audioBlob && (
                          <div className="text-center mb-2">
                            <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                              File ready: {audioBlob.name || 'Recording'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {(audioBlob.size / (1024 * 1024)).toFixed(2)} MB
                            </p>
                          </div>
                        )}
                        <Button
                          size="lg"
                          onClick={handleSubmit}
                          className="h-12 px-8 rounded-full bg-violet-600 hover:bg-violet-700"
                          disabled={!audioBlob}
                          data-testid="process-recording-button"
                        >
                          <Sparkles className="w-5 h-5 mr-2" />
                          Process Recording
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Chat toggle button — visible when meeting exists in live mode */}
                  {meetingId && liveMode && (status === 'recording' || status === 'stopped') && (
                    <Button
                      variant="outline"
                      className="mt-4 rounded-full"
                      onClick={() => setShowChat(!showChat)}
                      data-testid="toggle-chat-button"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      {showChat ? 'Hide Chat' : 'Ask AI about this meeting'}
                    </Button>
                  )}
                </div>

                {/* Upload Section */}
                {status === 'ready' && (
                  <div className="border-t border-slate-200 dark:border-slate-800 pt-6 mt-6">
                    <div className="text-center">
                      <p className="text-muted-foreground mb-4">{t('recording.uploadDesc')}</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="audio/*,video/*,.m4a,.mp4,.caf,.mp3,.wav,.aac,.ogg,.webm,.flac,.3gp,.amr"
                        onChange={handleFileUpload}
                        className="hidden"
                        data-testid="audio-file-input"
                      />
                      <Button
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        className="rounded-full"
                        data-testid="upload-audio-button"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {t('recording.upload')}
                      </Button>
                      <p className="text-xs text-muted-foreground mt-2">{t('recording.supportedFormats')}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Live Analysis + Chat */}
          {showRightPanel && (
            <div className="space-y-6">
              {/* Speaker & Topic Info */}
              {(speakerCount > 0 || topics.length > 0) && (
                <Card className="rounded-2xl border-slate-200 dark:border-slate-800">
                  <CardContent className="pt-5 pb-4">
                    <div className="space-y-3">
                      {speakerCount > 0 && (
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            {t('recording.speakersDetected') || 'Speakers detected'}:
                          </span>
                          <span className="text-sm font-medium">{speakerCount}</span>
                          {speakers.map((s, i) => (
                            <Badge key={s} variant="outline" className={`text-xs ${SPEAKER_TEXT_COLORS[i % SPEAKER_TEXT_COLORS.length]}`}>
                              {s}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {topics.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm text-muted-foreground">
                            {t('recording.topicsDetected') || 'Topics'}:
                          </span>
                          {topics.map((topic) => (
                            <Badge key={topic} variant="secondary" className="text-xs">
                              {topic}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Live Transcript Panel */}
              <Card className="rounded-2xl border-slate-200 dark:border-slate-800">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <FileText className="w-5 h-5 text-violet-600" />
                    {t('recording.liveTranscript') || 'Live Transcript'}
                    {isRecording && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
                    {analyzing && <Loader2 className="w-4 h-4 animate-spin text-violet-500" />}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[350px] pr-4">
                    <div className="space-y-3">
                      {liveTranscript.map((seg, i) => (
                        <div key={i} className={`border-l-2 ${getSpeakerColor(seg.speaker)} pl-3`}>
                          <span className={`text-xs font-medium ${getSpeakerTextColor(seg.speaker)}`}>
                            {seg.speaker}
                          </span>
                          <p className="text-sm text-foreground mt-0.5">{seg.text}</p>
                        </div>
                      ))}
                      {analyzing && liveTranscript.length > 0 && (
                        <div className="border-l-2 border-violet-500/30 pl-3 animate-pulse">
                          <span className="text-xs text-violet-500 font-mono">
                            {t('recording.analyzing') || 'Analyzing...'}
                          </span>
                        </div>
                      )}
                      <div ref={transcriptEndRef} />
                    </div>
                    {liveTranscript.length === 0 && !analyzing && (
                      <div className="text-center py-8 text-muted-foreground">
                        <Mic className="w-8 h-8 mx-auto mb-2 opacity-40" />
                        <p className="text-sm">
                          {isRecording
                            ? `${t('recording.firstAnalysisIn') || 'First analysis in'} ${nextAnalysisIn}s...`
                            : t('recording.transcriptWillAppear') || 'Transcript will appear here during recording...'}
                        </p>
                      </div>
                    )}
                    {liveTranscript.length === 0 && analyzing && (
                      <div className="text-center py-8 text-muted-foreground">
                        <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin opacity-60" />
                        <p className="text-sm">{t('recording.analyzingFirstChunk') || 'Analyzing first audio segment...'}</p>
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Chat Panel */}
              {showChat && meetingId && (
                <Card className="rounded-2xl border-slate-200 dark:border-slate-800">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <MessageSquare className="w-5 h-5 text-violet-600" />
                        AI Chat
                      </CardTitle>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setShowChat(false)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col h-[280px]">
                      <ScrollArea className="flex-1 pr-4">
                        <div className="space-y-3">
                          {chatMessages.length === 0 && (
                            <div className="text-center py-6 text-muted-foreground">
                              <p className="text-sm">Ask AI about this conversation...</p>
                            </div>
                          )}
                          {chatMessages.map((msg, i) => (
                            <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                              <div
                                className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                                  msg.role === 'user'
                                    ? 'bg-violet-600 text-white'
                                    : 'bg-slate-100 dark:bg-slate-800 text-foreground'
                                }`}
                              >
                                <p className="leading-relaxed">{msg.content}</p>
                              </div>
                            </div>
                          ))}
                          {chatLoading && (
                            <div className="flex gap-2 justify-start">
                              <div className="bg-slate-100 dark:bg-slate-800 rounded-xl px-3 py-2">
                                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                              </div>
                            </div>
                          )}
                          <div ref={chatEndRef} />
                        </div>
                      </ScrollArea>
                      <form
                        onSubmit={handleSendChat}
                        className="flex gap-2 pt-3 mt-3 border-t border-slate-200 dark:border-slate-800"
                      >
                        <Input
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          placeholder="Ask about the conversation..."
                          className="flex-1 h-9 rounded-lg text-sm"
                          disabled={chatLoading}
                          data-testid="chat-input"
                        />
                        <Button
                          type="submit"
                          size="sm"
                          className="h-9 px-3 rounded-lg bg-violet-600 hover:bg-violet-700"
                          disabled={chatLoading || !chatInput.trim()}
                          data-testid="chat-send-button"
                        >
                          {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                      </form>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Recording;
